import * as React from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import { AuthViewLogin, AuthViewLogout } from 'pages/auth/Auth.pages';
import { ErrorPage } from '@web-config-app/core-react-ui';
import {
  useConfigApp,
  useConfigAppPath,
  useAuthoringEnvironments,
  UserInfoProvider,
} from '@web-config-app/core-react';
import {
  buildTenantPath,
  isMasonryContentEnabled,
  SIGN_OUT_PATH,
} from '@web-config-app/core';
import type { ResolvedModule } from '@web-config-app/core';
import { ConfigAppEntityListPage } from '../pages/config-app-entity-list/config-app-entity-list.page';
import { ConfigAppEntityDetailsPage } from '../pages/config-app-entity-details/config-app-entity-details.page';
import { ConfigAppSettingsPage } from '../pages/config-app-entity-settings/config-app-entity-settings.component';
import { MultiTenantAuthorizedRoutes } from './multi-tenant-authorized-routes/multi-tenant-authorized-routes.component';
import { LandingRedirect } from './landing-redirect/landing-redirect.component';
import { EntityCreateRedirect } from './entity-create-redirect/entity-create-redirect.component';
import { EntityDetailsRedirect } from './entity-details-redirect/entity-details-redirect.component';
import { RootHandler } from './root-handler/root-handler.component';
import { LegacyUrlRedirect } from './legacy-url-redirect/legacy-url-redirect.component';
import { ValidateRouteParams } from './validate-route-params/validate-route-params.component';
import { TabPage } from './tab-page/tab-page.component';
import { ModuleRoutes } from './module-router/module-router.component';
import { PermissionsGuard } from './permissions-guard/permissions-guard.component';
import { captureErrorWithTenant } from '../utils/sentry-helpers';

/**
 * Fallback UI after `RouteErrorBoundary` catches an error. The user is sent to
 * the tenant landing URL by default. If the current location is already the
 * tenant landing page, this component renders `ErrorPage` instead of
 * redirecting, so the app does not enter an infinite redirect loop.
 *
 * This component does not handle unknown modules or environment keys in a
 * normal URL. Those cases are handled earlier by `ValidateRouteParams`, which
 * renders a tenant-aware `ErrorPage`. Entity list and detail pages wrap their
 * content in `react-error-boundary` inside their page containers. Errors there
 * are handled by that inner boundary and its fallback UI, and they do not
 * reach this redirect.
 */
const TenantFallbackRedirect = () => {
  const { tenantId } = useParams();
  const location = useLocation();
  const tenantLandingPath = buildTenantPath(tenantId);

  // If already on the tenant landing page, show a static error page
  // instead of redirecting to the same URL (which would loop).
  if (location.pathname === tenantLandingPath) {
    return <ErrorPage homeUrl="/" />;
  }

  return <Navigate to={tenantLandingPath} replace />;
};

const TenantErrorPage = () => {
  const { tenantId } = useParams();
  return <ErrorPage homeUrl={buildTenantPath(tenantId)} />;
};

/**
 * Error boundary for the tenant-scoped shell around
 * `MultiTenantAuthorizedRoutes` and nested routes. It catches render errors
 * only when no child error boundary handles them first. For example, entity
 * list and detail page containers install `react-error-boundary` around their
 * content, so most errors on those screens are handled there instead of here.
 *
 * When this boundary catches an error, it renders `TenantFallbackRedirect` so
 * the user leaves the failing route. The error is also reported to Sentry with
 * tenant and pathname context.
 *
 * The `resetKey` prop should track `location.pathname` so the boundary clears
 * its error state when the user navigates to a different path.
 */
export class RouteErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
    resetKey: string;
    tenantId: string | undefined;
  },
  { hasError: boolean }
> {
  constructor(props: {
    children: React.ReactNode;
    resetKey: string;
    tenantId: string | undefined;
  }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: { resetKey: string }) {
    const { resetKey } = this.props;
    const { hasError } = this.state;
    if (prevProps.resetKey !== resetKey && hasError) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { tenantId, resetKey } = this.props;
    captureErrorWithTenant(error, tenantId, {
      tags: { pathname: resetKey },
      context: {
        errorBoundary: { componentStack: errorInfo.componentStack ?? '' },
      },
    });
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return <TenantFallbackRedirect />;
    }
    return children;
  }
}

const AuthorizedAppWrapper = () => {
  const location = useLocation();
  const { tenantId } = useParams();
  // MultiTenantAuthorizedRoutes wraps ValidateRouteParams so it blocks
  // rendering until auth and permissions are ready. This prevents
  // ValidateRouteParams from crashing on configApp hooks during LeagueProvider
  // config transitions (discovery → tenant config).
  return (
    <RouteErrorBoundary resetKey={location.pathname} tenantId={tenantId}>
      <MultiTenantAuthorizedRoutes>
        <ValidateRouteParams>
          <UserInfoProvider>
            <Outlet />
          </UserInfoProvider>
        </ValidateRouteParams>
      </MultiTenantAuthorizedRoutes>
    </RouteErrorBoundary>
  );
};

/**
 * Renders ModuleRoutes for the non-entity modules within the current tab.
 * Entity routes are handled by explicit route patterns above this wildcard.
 */
const TabModuleRoutes = () => {
  const { tabId } = useParams();
  const { modules = [] } = useConfigApp();
  const currentTab = modules.find(
    (tabModule: ResolvedModule) => tabModule.id === tabId,
  );

  if (!currentTab || !currentTab.modules) return <ErrorPage />;

  return <ModuleRoutes modules={currentTab.modules} />;
};

/**
 * Redirects from the tab index to the first accessible child, honouring
 * YAML order. If the first child is a container, ContainerIndexRedirect
 * picks up from there — we don't reach past it to find an entity.
 */
const TabIndexRedirect = () => {
  const { tabId, tenantId } = useParams();
  const { modules = [], entityConfigs = {} } = useConfigApp();
  const generateConfigPaths = useConfigAppPath();
  const { defaultAuthoringEnvironment } = useAuthoringEnvironments();

  const currentTab = modules.find(
    (tabModule: ResolvedModule) => tabModule.id === tabId,
  );

  // Tab itself is an entity — redirect to its own entity list
  if (currentTab?.type === 'entity') {
    const { entityListPath } = generateConfigPaths({
      tabId,
      moduleId: undefined,
      environmentKey: defaultAuthoringEnvironment.environmentKey,
    });
    return <Navigate to={entityListPath} replace />;
  }

  const children = currentTab?.modules ?? [];
  const firstChild = children.find(
    (child) => child.type !== 'entity' || child.id in entityConfigs,
  );
  if (!firstChild) {
    return <ErrorPage homeUrl={buildTenantPath(tenantId)} />;
  }

  if (firstChild.type === 'entity') {
    const { entityListPath } = generateConfigPaths({
      tabId,
      moduleId: firstChild.id,
      environmentKey: defaultAuthoringEnvironment.environmentKey,
    });
    return <Navigate to={entityListPath} replace />;
  }

  return <Navigate to={firstChild.id} replace />;
};

/**
 * Layout route that enforces the parent tab's permissions on full-screen
 * entity routes (details, create, settings) that render outside TabPage.
 */
const TabPermissionsGuard = () => {
  const { tabId } = useParams();
  const { modules = [] } = useConfigApp();
  const currentTab = modules.find(
    (tabModule: ResolvedModule) => tabModule.id === tabId,
  );

  return (
    <PermissionsGuard permissions={currentTab?.permissions}>
      <Outlet />
    </PermissionsGuard>
  );
};

/**
 * Map of entity ids that are gated behind a feature flag for v1 rollout.
 * Each value is the current evaluation of the flag — re-evaluated per render
 * so a user toggling the flag in Admin sees the change immediately on
 * navigation.
 *
 * Used by `FeatureFlaggedEntityGuard` to intercept URL access to entities
 * that are filtered out of the side nav by the same flag — without it, a
 * bookmarked URL bypasses the nav-level gate in `tab-page.component.tsx`.
 *
 * When a flag promotion (auto-on for everyone) lands, remove the entry
 * here and the matching entry in `tab-page.component.tsx`.
 */
const useFeatureGatedEntities = (): Record<string, boolean> => ({
  'masonry-generic-content': isMasonryContentEnabled(),
});

/**
 * Renders an entity-route element only when the entity is not feature-gated
 * (or the gating flag is on). When gated off, renders the tenant ErrorPage
 * so a direct URL produces the same dead-end as an unknown entity.
 */
const FeatureFlaggedEntityGuard = ({
  children,
}: React.PropsWithChildren): React.ReactElement => {
  const { tenantId, childModuleId, moduleId } = useParams();
  const gated = useFeatureGatedEntities();
  // Container routes carry the entity id in `:childModuleId`; direct entity
  // routes under a tab carry it in `:moduleId`. Tab-as-entity routes resolve
  // the id from `:tabId` elsewhere — those don't apply here.
  const candidateEntityId = childModuleId ?? moduleId;
  if (candidateEntityId && gated[candidateEntityId] === false) {
    return <ErrorPage homeUrl={buildTenantPath(tenantId)} />;
  }
  return children as React.ReactElement;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/sign-in" element={<AuthViewLogin />} />
      <Route path={SIGN_OUT_PATH} element={<AuthViewLogout />} />

      {/* Tenant-scoped routes */}
      <Route path="/tenants/:tenantId" element={<AuthorizedAppWrapper />}>
        <Route index element={<LandingRedirect />} />

        {/* Tab layout — provides navigation bar + side nav */}
        <Route path=":tabId" element={<TabPage />}>
          <Route index element={<TabIndexRedirect />} />

          {/* Entity list inside tab layout (with side nav) */}
          {/* Tab is itself an entity: /:tabId/environments/... */}
          <Route
            path="environments/:environmentKey/list"
            element={<ConfigAppEntityListPage />}
          />
          {/* Direct entity under tab: /:tabId/:moduleId/environments/... */}
          <Route
            path=":moduleId/environments/:environmentKey/list"
            element={
              <FeatureFlaggedEntityGuard>
                <ConfigAppEntityListPage />
              </FeatureFlaggedEntityGuard>
            }
          />
          {/* Entity inside container: /:tabId/:moduleId/:childModuleId/environments/... */}
          <Route
            path=":moduleId/:childModuleId/environments/:environmentKey/list"
            element={
              <FeatureFlaggedEntityGuard>
                <ConfigAppEntityListPage />
              </FeatureFlaggedEntityGuard>
            }
          />

          {/* Non-entity module routes (schema pages, component pages, containers) */}
          <Route path="*" element={<TabModuleRoutes />} />
        </Route>

        {/* Entity details, create, and settings render full-screen without the sidebar.
            TabPermissionsGuard enforces the parent tab's permissions. */}
        <Route path=":tabId" element={<TabPermissionsGuard />}>
          {/* Tab is itself an entity */}
          <Route
            path="environments/:environmentKey/create"
            element={<EntityCreateRedirect />}
          />
          <Route
            path="environments/:environmentKey/details/:entityInstanceId"
            element={<EntityDetailsRedirect />}
          />
          <Route
            path="environments/:environmentKey/create/locale/:localeId"
            element={<ConfigAppEntityDetailsPage />}
          />
          <Route
            path="environments/:environmentKey/details/:entityInstanceId/locale/:localeId"
            element={<ConfigAppEntityDetailsPage />}
          />
          <Route
            path="environments/:environmentKey/settings"
            element={<ConfigAppSettingsPage />}
          />
          {/* Direct entity under tab */}
          <Route
            path=":moduleId/environments/:environmentKey/create"
            element={
              <FeatureFlaggedEntityGuard>
                <EntityCreateRedirect />
              </FeatureFlaggedEntityGuard>
            }
          />
          <Route
            path=":moduleId/environments/:environmentKey/details/:entityInstanceId"
            element={
              <FeatureFlaggedEntityGuard>
                <EntityDetailsRedirect />
              </FeatureFlaggedEntityGuard>
            }
          />
          <Route
            path=":moduleId/environments/:environmentKey/create/locale/:localeId"
            element={
              <FeatureFlaggedEntityGuard>
                <ConfigAppEntityDetailsPage />
              </FeatureFlaggedEntityGuard>
            }
          />
          <Route
            path=":moduleId/environments/:environmentKey/details/:entityInstanceId/locale/:localeId"
            element={
              <FeatureFlaggedEntityGuard>
                <ConfigAppEntityDetailsPage />
              </FeatureFlaggedEntityGuard>
            }
          />
          <Route
            path=":moduleId/environments/:environmentKey/settings"
            element={
              <FeatureFlaggedEntityGuard>
                <ConfigAppSettingsPage />
              </FeatureFlaggedEntityGuard>
            }
          />
          {/* Entity inside container */}
          <Route
            path=":moduleId/:childModuleId/environments/:environmentKey/create"
            element={
              <FeatureFlaggedEntityGuard>
                <EntityCreateRedirect />
              </FeatureFlaggedEntityGuard>
            }
          />
          <Route
            path=":moduleId/:childModuleId/environments/:environmentKey/details/:entityInstanceId"
            element={
              <FeatureFlaggedEntityGuard>
                <EntityDetailsRedirect />
              </FeatureFlaggedEntityGuard>
            }
          />
          <Route
            path=":moduleId/:childModuleId/environments/:environmentKey/create/locale/:localeId"
            element={
              <FeatureFlaggedEntityGuard>
                <ConfigAppEntityDetailsPage />
              </FeatureFlaggedEntityGuard>
            }
          />
          <Route
            path=":moduleId/:childModuleId/environments/:environmentKey/details/:entityInstanceId/locale/:localeId"
            element={
              <FeatureFlaggedEntityGuard>
                <ConfigAppEntityDetailsPage />
              </FeatureFlaggedEntityGuard>
            }
          />
          <Route
            path=":moduleId/:childModuleId/environments/:environmentKey/settings"
            element={<ConfigAppSettingsPage />}
          />
        </Route>

        <Route path="*" element={<TenantErrorPage />} />
      </Route>

      {/* Root: handles Auth0 discovery login and redirects to the resolved tenant route */}
      <Route path="/" element={<RootHandler />} />

      {/* Legacy domain URLs without tenant prefix */}
      <Route path="/domains/*" element={<LegacyUrlRedirect />} />

      <Route path="*" element={<ErrorPage />} />
    </Routes>
  );
}

export default AppRoutes;
